import React, { type ComponentType, Fragment } from 'react';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import {
	ExtensionMode,
	type ExtensionParameters,
	type HubDefaultParameters,
	HubExtensionsWrapper,
	type ProductDependenciesProviderProps,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { type CardsExtensionParameters } from '../../types';

import { LinkCardsContent } from './cards-content';

type CardsExtensionContainerProps = {
	contentId: string;
	extensionNode?: ExtensionParams<CardsExtensionParameters>;
	mode: ExtensionMode;
};

export const CardsExtensionBase = ({
	extensionNode,
	contentId,
	mode = ExtensionMode.RENDERER,
}: CardsExtensionContainerProps): React.JSX.Element | null => {
	const { isTargetAudience, isCompanyHub, smartCardProviderComponent } = useProductDependencies();

	const SmartCardProvider = smartCardProviderComponent || Fragment;

	const { parameters, localId = '' } = extensionNode || {};

	if (!parameters) {
		return null;
	}

	return (
		<HubExtensionsWrapper<CardsExtensionParameters>
			mode={mode}
			extensionNode={extensionNode}
			contentId={contentId}
			showNoDataState={!parameters}
			packageName="cards-extension"
			analyticsAttributes={{
				renderVariant: parameters?.renderVariant,
				version: parameters?.version,
				isCompanyHub: isCompanyHub,
				hubSegementationApplied: parameters?.isSegmentationShown,
			}}
		>
			<SmartCardProvider>
				<LinkCardsContent
					extensionLocalId={localId}
					parameters={parameters}
					isCompanyHub={isCompanyHub}
					isTargetAudience={isTargetAudience}
					isInViewMode={mode === ExtensionMode.RENDERER}
					contentId={contentId}
				/>
			</SmartCardProvider>
		</HubExtensionsWrapper>
	);
};

export type CardsExtensionProps = ExtensionParameters<CardsExtensionParameters> & {
	defaultParameters?: HubDefaultParameters;
	propsProvider: ComponentType<ProductDependenciesProviderProps>;
} & Pick<CardsExtensionContainerProps, 'mode'>;

export const CardsExtension = ({
	propsProvider: PropsProvider,
	...props
}: CardsExtensionProps): React.JSX.Element => (
	<PropsProvider {...props}>
		<CardsExtensionBase {...props} />
	</PropsProvider>
);
